import { useStore } from '@nanostores/react';
import { memo } from 'react';
import { Dialog, DialogActions, DialogButton, DialogContent, DialogHeader } from '~/components/ui/Dialog';
import { useGithubStore } from '~/lib/context/github';
import { useProjectVisibility } from '~/lib/hooks/useProjectVisibility';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';

interface DiscordSupportModalProps {
  open: boolean;
  onClose: () => void;
}

export const DiscordSupportModal = memo(({ open, onClose }: DiscordSupportModalProps) => {
  const { onProjectVisibilityChange } = useProjectVisibility();
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const maxVisibility = activeOrganization?.organizationSettings?.maxVisibility ?? 'public';

  const github = useGithubStore();
  const githubConnectedTo = useStore(github.connection);
  const permissions = useStore(projectStore.permissions);
  const currentVisibility = useStore(projectStore.visibility);

  const canChangeVisibility = !githubConnectedTo.repo && permissions.canChangeVisibility;
  const isProjectAccessible = currentVisibility === 'public' || currentVisibility === 'secret';

  const handleJoinDiscord = () => {
    window.open('https://discord.com/invite/stackblitz', '_blank');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={500}>
      <DialogHeader title="Get Help on Discord" description="Join our community for assistance" badge="warning" />
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 bg-bolt-elements-background-depth-1 p-4 rounded-md">
            <span className="i-bolt:logos-discord?mask size-5 text-bolt-elements-textSecondary mt-0.5 shrink-0" />
            <div>
              <h3 className="text-bolt-elements-textPrimary font-medium mb-1">Join our Discord community</h3>
              <p className="text-bolt-elements-textSecondary text-sm">
                Post your question in the <strong>#help</strong> channel to get assistance from our community.
              </p>
            </div>
          </div>

          {canChangeVisibility && !isProjectAccessible && (
            <div className="flex items-start gap-3 bg-bolt-elements-background-depth-1 p-4 rounded-md">
              <span className="i-heroicons:share-solid size-5 text-bolt-elements-textSecondary mt-0.5 shrink-0" />
              <div>
                <h3 className="text-bolt-elements-textPrimary font-medium mb-1">Make your project accessible</h3>
                <p className="text-bolt-elements-textSecondary text-sm">
                  Please make your project{' '}
                  {maxVisibility === 'public' && (
                    <button
                      onClick={() => onProjectVisibilityChange('public')}
                      className="text-bolt-ds-brand-highlight hover:underline bg-transparent border-none p-0"
                    >
                      public
                    </button>
                  )}
                  {maxVisibility === 'public' && ' or '}
                  {['public', 'secret'].includes(maxVisibility) && (
                    <button
                      onClick={() => onProjectVisibilityChange('secret')}
                      className="text-bolt-ds-brand-highlight hover:underline bg-transparent border-none p-0"
                    >
                      accessible by URL
                    </button>
                  )}
                  . This will allow anyone trying to help you easily reproduce the issue and provide assistance.
                </p>
              </div>
            </div>
          )}

          {isProjectAccessible && (
            <div className="flex items-start gap-3 bg-bolt-elements-background-depth-1 p-4 rounded-md">
              <span className="i-ph:share size-5 text-bolt-elements-textSecondary mt-0.5" />
              <div>
                <h3 className="text-bolt-elements-textPrimary font-medium mb-1">Share your project URL</h3>
                <p className="text-bolt-elements-textSecondary text-sm">
                  Please don't forget to share your project URL with us! This will allow anyone trying to help you
                  easily reproduce the issue and provide assistance.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <DialogButton variant="secondary" onClick={onClose}>
          Cancel
        </DialogButton>
        <DialogButton variant="primary" onClick={handleJoinDiscord}>
          Join Discord
        </DialogButton>
      </DialogActions>
    </Dialog>
  );
});
